'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { Link as IntlLink } from '@/i18n/navigation';
import { CONTACT_EMAIL, DOCS_URL } from '@/lib/constants';

import { SceneCanvas } from '../SceneCanvas';
import { usePrefersReducedMotion } from '../usePrefersReducedMotion';

const MONO = "'JetBrains Mono', monospace";
const HELV = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const mailto = (subject: string) =>
	`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;

/** Structural (non-translated) config — HUD codes, tags, scene ids, domains. */
export interface VerifyMeta {
	tag: string;
	scene: string;
	soon?: boolean;
}
export interface AmlMeta {
	tag: string;
}
export interface Aggregator {
	name: string;
	domain: string;
}
export interface IndustryConfig {
	/** HUD brand line, e.g. "ONEKYC // INDUSTRY_01". */
	brand: string;
	/** HUD pill, e.g. "CHAIN · CRYPTO". */
	tag: string;
	/** Hero / AML background <scene-canvas> id. */
	heroScene: string;
	/** Deploy mailto subject + tag used in HUD codes (e.g. "CRYPTO"). */
	deployTag: string;
	verify: VerifyMeta[];
	aml: AmlMeta[];
	aggregators: Aggregator[];
}

// i18n copy shapes (parallel-indexed with the structural config above).
interface VerifyCopy {
	name: string;
	desc: string;
	chips: string[];
}
interface NamedCopy {
	name: string;
	desc: string;
}

const lines = (titleLines: string[]) =>
	titleLines.map((line, i) => (
		<span key={i}>
			{i > 0 && <br />}
			{line}
		</span>
	));

const num = (i: number) => String(i + 1).padStart(2, '0');

interface IndustryPageProps {
	namespace: string;
	config: IndustryConfig;
}

/**
 * Shared industry page (Crypto, and future verticals) — ported 1:1 from the
 * design and driven by the given i18n `namespace`. Theme-aware like ProductPage:
 * the light "paper" chrome uses --color-* tokens so it flips for dark mode,
 * while the dark HUD bands stay dark in both themes (lifted border via the
 * `.product-page .pp-panel` rule). Structural HUD codes / tags / scene ids /
 * aggregator domains live in `config`, not i18n.
 */
export function IndustryPage({ namespace, config }: IndustryPageProps) {
	const t = useTranslations(namespace);
	const reducedMotion = usePrefersReducedMotion();

	const statusSteps = t.raw('hero.statusSteps') as string[];
	const audience = t.raw('hero.audience') as string[];
	const metrics = t.raw('metrics') as [string, string][];
	const verifyItems = t.raw('verify.items') as VerifyCopy[];
	const steps = t.raw('integration.steps') as string[];
	const amlItems = t.raw('aml.items') as NamedCopy[];
	const flowItems = t.raw('flow.items') as NamedCopy[];
	const soon = t('soon');

	const [si, setSi] = useState(0);
	useEffect(() => {
		if (reducedMotion) return;
		const id = setInterval(
			() => setSi((s) => (s + 1) % statusSteps.length),
			1500,
		);
		return () => clearInterval(id);
	}, [reducedMotion, statusSteps.length]);

	const kicker: React.CSSProperties = {
		fontFamily: MONO,
		fontSize: '11px',
		letterSpacing: '2px',
		color: 'var(--color-muted)',
	};
	const darkBtn: React.CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		padding: '16px 24px',
		background: '#f2f2f4',
		color: '#0a0a0b',
		border: 'none',
		cursor: 'pointer',
		fontFamily: MONO,
		fontSize: '12px',
		letterSpacing: '2px',
		textDecoration: 'none',
	};
	const ghostBtn: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '16px 24px',
		background: 'transparent',
		color: '#f2f2f4',
		border: '1px solid rgba(255,255,255,.3)',
		cursor: 'pointer',
		fontFamily: MONO,
		fontSize: '12px',
		letterSpacing: '2px',
		textDecoration: 'none',
	};
	const sectionWrap: React.CSSProperties = {
		maxWidth: '1340px',
		margin: '0 auto',
		padding: '44px clamp(18px,5vw,72px) 20px',
	};
	const divider = (
		<div
			style={{
				height: '1px',
				background: 'var(--color-border)',
				marginTop: '20px',
			}}
		/>
	);

	return (
		<div
			className="product-page"
			style={{
				background: 'var(--color-bg)',
				color: 'var(--color-fg)',
				fontFamily: HELV,
			}}
		>
			{/* ── HERO (dark in both themes) ── */}
			<div
				style={{
					position: 'relative',
					overflow: 'hidden',
					background: '#0a0a0b',
					color: '#f2f2f4',
					minHeight: '800px',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<SceneCanvas
					scene={config.heroScene}
					className="pointer-events-none absolute top-0 right-0 h-full w-[min(74%,940px)]"
				/>
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						inset: 0,
						background:
							'linear-gradient(100deg, #0a0a0b 32%, rgba(10,10,11,.55) 62%, rgba(10,10,11,.18))',
						pointerEvents: 'none',
					}}
				/>
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						height: '1px',
						background: 'rgba(255,255,255,.14)',
						overflow: 'hidden',
					}}
				>
					<div
						className="onekyc-sweep"
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '34%',
							height: '1px',
							background:
								'linear-gradient(90deg,transparent,rgba(255,255,255,.9),transparent)',
						}}
					/>
				</div>

				<div
					style={{
						position: 'relative',
						zIndex: 1,
						display: 'flex',
						justifyContent: 'space-between',
						gap: '16px',
						padding: 'clamp(96px,12vw,116px) clamp(18px,5vw,72px) 24px',
						fontFamily: MONO,
						fontSize: '11px',
						letterSpacing: '1.5px',
						color: 'rgba(242,242,244,.6)',
					}}
				>
					<span>{config.brand}</span>
					<span
						style={{
							border: '1px solid rgba(255,255,255,.25)',
							padding: '4px 10px',
							alignSelf: 'flex-start',
						}}
					>
						[ {config.tag} ]
					</span>
				</div>

				<div
					style={{
						position: 'relative',
						zIndex: 1,
						marginTop: 'auto',
						padding: '0 clamp(18px,5vw,72px) clamp(36px,5vw,64px)',
						maxWidth: '960px',
					}}
				>
					<div
						style={{
							fontFamily: MONO,
							fontSize: '12px',
							letterSpacing: '3px',
							color: 'rgba(242,242,244,.6)',
						}}
					>
						{t('hero.kicker')}
					</div>
					<h1
						style={{
							margin: '14px 0 0',
							fontWeight: 400,
							fontSize: 'clamp(30px,8vw,104px)',
							letterSpacing: '-.04em',
							lineHeight: 0.9,
						}}
					>
						{lines(t.raw('hero.titleLines') as string[])}
					</h1>
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: '8px',
							marginTop: '20px',
						}}
					>
						{audience.map((a) => (
							<span
								key={a}
								style={{
									fontFamily: MONO,
									fontSize: '11px',
									letterSpacing: '1px',
									color: 'rgba(242,242,244,.85)',
									border: '1px solid rgba(255,255,255,.22)',
									padding: '8px 13px',
								}}
							>
								{a}
							</span>
						))}
					</div>
					<p
						style={{
							margin: '22px 0 0',
							maxWidth: '54ch',
							fontSize: 'clamp(16px,1.9vw,20px)',
							lineHeight: 1.6,
							color: 'rgba(242,242,244,.8)',
						}}
					>
						{t('hero.desc')}
					</p>
					<div
						style={{
							fontFamily: MONO,
							fontSize: '12px',
							letterSpacing: '1px',
							color: 'rgba(242,242,244,.55)',
							marginTop: '18px',
						}}
					>
						&gt; {statusSteps[si % statusSteps.length]}{' '}
						<span className="onekyc-blink" aria-hidden="true">
							_
						</span>
					</div>
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10px',
							marginTop: '24px',
						}}
					>
						<a
							href={mailto(`${config.deployTag} Integration Request`)}
							style={darkBtn}
						>
							{t('hero.ctaConnect')} <span aria-hidden="true">-&gt;</span>
						</a>
						<a
							href={DOCS_URL}
							target="_blank"
							rel="noopener noreferrer"
							style={ghostBtn}
						>
							{t('hero.ctaDocs')}
						</a>
					</div>
				</div>
			</div>

			{/* ── METRICS BAR ── */}
			<div style={{ borderBottom: '1px solid var(--color-border)' }}>
				<div
					style={{
						maxWidth: '1340px',
						margin: '0 auto',
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
					}}
				>
					{metrics.map(([v, l], i) => (
						<div
							key={i}
							style={{
								padding: '28px clamp(16px,2.4vw,28px)',
								borderLeft: '1px solid var(--color-border)',
							}}
						>
							<div
								style={{
									fontFamily: MONO,
									fontSize: 'clamp(24px,3vw,40px)',
									fontWeight: 700,
									letterSpacing: '-.02em',
									lineHeight: 1,
								}}
							>
								{v}
							</div>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '10.5px',
									letterSpacing: '1.5px',
									color: 'var(--color-muted)',
									marginTop: '12px',
									textTransform: 'uppercase',
								}}
							>
								{l}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── VERIFY ── */}
			<div
				style={{
					maxWidth: '1340px',
					margin: '0 auto',
					padding: '64px clamp(18px,5vw,72px) 20px',
				}}
			>
				<div style={kicker}>SYS.01 // VERIFY</div>
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						gap: '10px 20px',
						marginTop: '8px',
					}}
				>
					<h2
						style={{
							margin: 0,
							fontWeight: 400,
							fontSize: 'clamp(28px,4.6vw,52px)',
							letterSpacing: '-.03em',
							lineHeight: 1,
						}}
					>
						{lines(t.raw('verify.titleLines') as string[])}
					</h2>
					<p
						style={{
							margin: 0,
							maxWidth: '36ch',
							fontSize: '14px',
							lineHeight: 1.6,
							color: 'var(--color-accent)',
							paddingBottom: '4px',
						}}
					>
						{t('verify.lead')}
					</p>
				</div>
				<div
					style={{
						height: '1px',
						background: 'var(--color-border)',
						marginTop: '22px',
					}}
				/>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
						gap: '14px',
						marginTop: '22px',
					}}
				>
					{config.verify.map((v, i) => {
						const copy = verifyItems[i];
						return (
							<div
								key={v.tag}
								className="pp-panel"
								style={{
									position: 'relative',
									overflow: 'hidden',
									background: '#0a0a0b',
									color: '#f2f2f4',
									minHeight: '330px',
									padding: '26px',
									display: 'flex',
									flexDirection: 'column',
								}}
							>
								<SceneCanvas
									scene={v.scene}
									className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.36]"
								/>
								<div
									aria-hidden="true"
									style={{
										position: 'absolute',
										inset: 0,
										background:
											'linear-gradient(180deg, rgba(10,10,11,.35), rgba(10,10,11,.82))',
										pointerEvents: 'none',
									}}
								/>
								<div
									style={{
										position: 'relative',
										zIndex: 1,
										display: 'flex',
										flexDirection: 'column',
										height: '100%',
									}}
								>
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											fontFamily: MONO,
											fontSize: '10.5px',
											letterSpacing: '1.5px',
											color: 'rgba(242,242,244,.6)',
										}}
									>
										<span>{num(i)}</span>
										<span>[ {v.tag} ]</span>
									</div>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '12px',
											flexWrap: 'wrap',
											marginTop: 'auto',
										}}
									>
										<span
											style={{
												fontSize: '24px',
												fontWeight: 500,
												letterSpacing: '-.02em',
											}}
										>
											{copy.name}
										</span>
										{v.soon && (
											<span
												style={{
													fontFamily: MONO,
													fontSize: '10px',
													letterSpacing: '1.5px',
													color: '#0a0a0b',
													background: '#f2f2f4',
													padding: '5px 9px',
												}}
											>
												{soon}
											</span>
										)}
									</div>
									<p
										style={{
											margin: '12px 0 0',
											fontSize: '14px',
											lineHeight: 1.6,
											color: 'rgba(242,242,244,.74)',
										}}
									>
										{copy.desc}
									</p>
									<div
										style={{
											display: 'flex',
											flexWrap: 'wrap',
											gap: '8px',
											marginTop: '18px',
										}}
									>
										{copy.chips.map((ch) => (
											<span
												key={ch}
												style={{
													fontFamily: MONO,
													fontSize: '10px',
													letterSpacing: '.5px',
													color: 'rgba(242,242,244,.8)',
													border: '1px solid rgba(255,255,255,.18)',
													padding: '6px 10px',
												}}
											>
												{ch}
											</span>
										))}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* ── INTEGRATION ── */}
			<div style={sectionWrap}>
				<div style={kicker}>SYS.02 // INTEGRATION</div>
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						gap: '10px 20px',
						marginTop: '8px',
					}}
				>
					<h2
						style={{
							margin: 0,
							fontWeight: 400,
							fontSize: 'clamp(28px,4vw,46px)',
							letterSpacing: '-.025em',
							lineHeight: 1,
						}}
					>
						{t('integration.title')}
					</h2>
					<p
						style={{
							margin: 0,
							maxWidth: '36ch',
							fontSize: '14px',
							lineHeight: 1.6,
							color: 'var(--color-accent)',
							paddingBottom: '4px',
						}}
					>
						{t('integration.lead')}
					</p>
				</div>
				{divider}

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
						gap: '14px',
						marginTop: '22px',
					}}
				>
					<div
						className="pp-panel"
						style={{
							background: '#0a0a0b',
							color: '#f2f2f4',
							padding: '26px',
							minHeight: '200px',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
						}}
					>
						<div
							style={{
								fontFamily: MONO,
								fontSize: '11px',
								letterSpacing: '1.5px',
								color: 'rgba(242,242,244,.55)',
							}}
						>
							{t('integration.aggregatorsLabel')}
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '12px',
								marginTop: '18px',
							}}
						>
							{config.aggregators.map((a) => (
								<div
									key={a.domain}
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										gap: '12px',
										border: '1px solid rgba(255,255,255,.16)',
										padding: '16px 18px',
									}}
								>
									<span
										style={{
											display: 'flex',
											flexDirection: 'column',
											gap: '3px',
										}}
									>
										<span
											style={{
												fontSize: '17px',
												fontWeight: 500,
												letterSpacing: '-.01em',
											}}
										>
											{a.name}
										</span>
										<span
											style={{
												fontFamily: MONO,
												fontSize: '10px',
												letterSpacing: '.5px',
												color: 'rgba(242,242,244,.5)',
											}}
										>
											{a.domain}
										</span>
									</span>
									<span
										style={{
											fontFamily: MONO,
											fontSize: '10px',
											letterSpacing: '1.5px',
											color: 'rgba(242,242,244,.85)',
											border: '1px solid rgba(255,255,255,.3)',
											padding: '5px 9px',
											whiteSpace: 'nowrap',
										}}
									>
										{soon}
									</span>
								</div>
							))}
						</div>
						<div
							style={{
								fontFamily: MONO,
								fontSize: '10.5px',
								letterSpacing: '.5px',
								color: 'rgba(242,242,244,.5)',
								marginTop: '18px',
							}}
						>
							{t('integration.aggregatorsNote')}
						</div>
					</div>

					<div
						className="pp-card"
						style={{
							padding: '26px',
							minHeight: '200px',
							boxShadow: 'none',
						}}
					>
						<div
							style={{
								fontFamily: MONO,
								fontSize: '11px',
								letterSpacing: '1.5px',
								color: 'var(--color-muted)',
							}}
						>
							{t('integration.stepsLabel')}
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '2px',
								marginTop: '16px',
								borderTop: '1px solid var(--color-border)',
							}}
						>
							{steps.map((s, i) => (
								<div
									key={i}
									style={{
										display: 'flex',
										gap: '16px',
										alignItems: 'flex-start',
										padding: '15px 0',
										borderBottom: '1px solid var(--color-border)',
									}}
								>
									<span
										style={{
											fontFamily: MONO,
											fontSize: '12px',
											fontWeight: 700,
											color: 'var(--color-fg)',
											flex: '0 0 auto',
										}}
									>
										{num(i)}
									</span>
									<span
										style={{
											fontSize: '14.5px',
											lineHeight: 1.5,
											color: 'var(--color-accent)',
										}}
									>
										{s}
									</span>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* ── AML IN DASHBOARD ── */}
			<div style={sectionWrap}>
				<div style={kicker}>SYS.03 // AML IN DASHBOARD</div>
				<div
					className="pp-panel"
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
						gap: '2px',
						marginTop: '16px',
						background: '#0b0b0c',
					}}
				>
					<div
						style={{
							position: 'relative',
							overflow: 'hidden',
							background: '#0a0a0b',
							color: '#f2f2f4',
							minHeight: '300px',
							padding: '28px',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<SceneCanvas
							scene={config.heroScene}
							className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
						/>
						<div
							aria-hidden="true"
							style={{
								position: 'absolute',
								inset: 0,
								background:
									'linear-gradient(180deg, rgba(10,10,11,.45), rgba(10,10,11,.85))',
								pointerEvents: 'none',
							}}
						/>
						<div
							style={{
								position: 'relative',
								zIndex: 1,
								display: 'flex',
								flexDirection: 'column',
								height: '100%',
							}}
						>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '10.5px',
									letterSpacing: '1.5px',
									color: 'rgba(242,242,244,.6)',
								}}
							>
								DASHBOARD // AML
							</div>
							<h2
								style={{
									margin: 'auto 0 0',
									fontWeight: 400,
									fontSize: 'clamp(26px,3.4vw,38px)',
									letterSpacing: '-.025em',
									lineHeight: 1.05,
								}}
							>
								{lines(t.raw('aml.titleLines') as string[])}
							</h2>
							<p
								style={{
									margin: '14px 0 0',
									maxWidth: '42ch',
									fontSize: '14.5px',
									lineHeight: 1.6,
									color: 'rgba(242,242,244,.74)',
								}}
							>
								{t('aml.desc')}
							</p>
						</div>
					</div>
					<div
						style={{
							background: '#0a0a0b',
							color: '#f2f2f4',
							padding: '28px',
							display: 'flex',
							flexDirection: 'column',
							gap: '12px',
						}}
					>
						{config.aml.map((a, i) => (
							<div
								key={a.tag}
								style={{
									border: '1px solid rgba(255,255,255,.16)',
									padding: '18px',
								}}
							>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										fontFamily: MONO,
										fontSize: '10px',
										letterSpacing: '1.5px',
										color: 'rgba(242,242,244,.55)',
									}}
								>
									<span>{a.tag}</span>
									<span>{num(i)}</span>
								</div>
								<div
									style={{
										fontSize: '16px',
										fontWeight: 500,
										letterSpacing: '-.01em',
										marginTop: '12px',
									}}
								>
									{amlItems[i].name}
								</div>
								<p
									style={{
										margin: '7px 0 0',
										fontSize: '13px',
										lineHeight: 1.55,
										color: 'rgba(242,242,244,.66)',
									}}
								>
									{amlItems[i].desc}
								</p>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* ── FLOW ── */}
			<div
				style={{
					maxWidth: '1340px',
					margin: '0 auto',
					padding: '44px clamp(18px,5vw,72px) 64px',
				}}
			>
				<div style={kicker}>SYS.04 // FLOW</div>
				<h2
					style={{
						margin: '8px 0 0',
						fontWeight: 400,
						fontSize: 'clamp(26px,3.6vw,42px)',
						letterSpacing: '-.025em',
						lineHeight: 1,
					}}
				>
					{t('flow.title')}
				</h2>
				{divider}
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
						gap: '14px',
						marginTop: '22px',
					}}
				>
					{flowItems.map((p, i) => (
						<div
							key={p.name}
							className="pp-card"
							style={{
								padding: '20px',
								minHeight: '150px',
								display: 'flex',
								flexDirection: 'column',
								boxShadow: 'none',
							}}
						>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '11px',
									letterSpacing: '1.5px',
									color: 'var(--color-muted)',
								}}
							>
								{num(i)}
							</div>
							<div
								style={{
									fontSize: '16px',
									fontWeight: 500,
									letterSpacing: '-.01em',
									marginTop: '14px',
								}}
							>
								{p.name}
							</div>
							<p
								style={{
									margin: '8px 0 0',
									fontSize: '12.5px',
									lineHeight: 1.5,
									color: 'var(--color-muted)',
								}}
							>
								{p.desc}
							</p>
						</div>
					))}
				</div>
			</div>

			{/* ── DEPLOY CTA (dark in both themes) ── */}
			<div style={{ background: '#0a0a0b', color: '#f2f2f4' }}>
				<div
					style={{
						maxWidth: '1340px',
						margin: '0 auto',
						padding: 'clamp(40px,6vw,80px) clamp(18px,5vw,72px)',
						position: 'relative',
						overflow: 'hidden',
					}}
				>
					<div
						aria-hidden="true"
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							height: '1px',
							background: 'rgba(255,255,255,.14)',
							overflow: 'hidden',
						}}
					>
						<div
							className="onekyc-sweep"
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '34%',
								height: '1px',
								background:
									'linear-gradient(90deg,transparent,rgba(255,255,255,.9),transparent)',
							}}
						/>
					</div>
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							justifyContent: 'space-between',
							alignItems: 'flex-end',
							gap: '24px',
						}}
					>
						<div>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '11px',
									letterSpacing: '2px',
									color: 'rgba(242,242,244,.5)',
								}}
							>
								DEPLOY // {config.deployTag}
							</div>
							<h2
								style={{
									margin: '12px 0 0',
									fontWeight: 400,
									fontSize: 'clamp(28px,4.4vw,52px)',
									letterSpacing: '-.025em',
									lineHeight: 1,
								}}
							>
								{lines(t.raw('deploy.titleLines') as string[])}
							</h2>
						</div>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
							<a
								href={mailto(`${config.deployTag} Access Request`)}
								style={darkBtn}
							>
								{t('deploy.ctaAccess')} <span aria-hidden="true">-&gt;</span>
							</a>
							<IntlLink
								href={{ pathname: '/', hash: 'pricing' }}
								style={ghostBtn}
							>
								{t('deploy.ctaPricing')}
							</IntlLink>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
